import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import { LinkProps, SimplePaletteColorOptions, colors, createTheme } from '@mui/material';
import { Severity } from '../../common/enums/Severity';
import { TestValueWrapper } from '../../common/utils/TestValueWrapper';
import { A11yLink } from '../../routing/A11yLink';

export type SeverityColors = { main: string; background: string; fadedBackground: string; text: string; filledText: string };

type SeverityPalette = Record<Severity, SeverityColors>;

declare module '@mui/material/styles' {
  interface Palette {
    screenBackground: SimplePaletteColorOptions;
    severities: SeverityPalette;
  }

  interface PaletteOptions {
    screenBackground: SimplePaletteColorOptions;
    severities: SeverityPalette;
  }

  interface BreakpointOverrides {
    xs: true;
    sm: true;
    ms: true;
    md: true;
    lg: true;
    xl: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    'light-grey': true;
  }
}

export const palette = {
  // MUI Theme overrides
  // See https://mui.com/material-ui/customization/default-theme/
  primary: {
    light: '#5cb7ff',
    main: '#1254A1',
    dark: '#1565c0',
    contrastText: '#fff',
  },
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.65)',
    disabled: 'rgba(0, 0, 0, 0.55)',
  },
  action: {
    active: 'rgba(0, 0, 0, 0.54)',
    hover: 'rgba(0, 0, 0, 0.04)',
    hoverOpacity: 0.04,
    selected: 'rgba(0, 0, 0, 0.08)',
    selectedOpacity: 0.08,
    disabled: 'rgba(0, 0, 0, 0.38)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
    disabledOpacity: 0.38,
    focus: 'rgba(0, 0, 0, 0.12)',
    focusOpacity: 0.12,
    activatedOpacity: 0.12,
  },
  // Custom values
  integrationChip: {
    active: '#dcedc8',
    inactive: '#ECEFF1',
  },
  backdrops: {
    light: '#fdfdfd',
    issues: '#E0E0E0',
    grey: 'rgba(0,0,0,0.04)',
    lightGrey: '#F6F8FB',
    transparentWhite: 'rgba(255,255,255,0.5)',
    accordionGrey: 'rgba(249, 250, 251, 1)',
    searchInput: 'rgba(30,30,30,0.05)',
    expandedRow: 'rgba(25, 118, 210, 0.08)',
  },
  jsonViewer: {
    mainColor: '#666666',
    background: '#F5F5F5B8',
    valueColor: '#006100',
    lineColor: '#C9C9C9',
    nullColor: '#5a5a5a',
    defaultColor: '#F2FAF2',
    blue: '#0067cc',
  },
  navbarBackground: {
    main: '#000',
  },
  screenBackground: {
    main: '#f8f8f8',
  },
  severities: {
    high: {
      main: '#D32F2F',
      background: '#FFDFDD',
      fadedBackground: '#FFF6F5',
      text: '#212121',
      filledText: '#FFFFFF',
    },
    med: {
      main: '#EF6C00',
      background: '#FDEAD8',
      fadedBackground: '#FFF9F5',
      text: '#212121',
      filledText: '#212121',
    },
    low: {
      main: '#FFEB3B',
      background: '#FFF8DB',
      fadedBackground: '#FFFDF6',
      text: '#212121',
      filledText: '#212121',
    },
    passed: {
      main: '#2E7D32',
      background: '#D9EDD7',
      fadedBackground: '#F4F8F5',
      text: '#212121',
      filledText: '#FFFFFF',
    },
    disabled: {
      main: '#78909C',
      background: '#ECEFF1',
      fadedBackground: '#ECEFF1',
      text: '#212121',
      filledText: '#000000',
    },
    filtered: {
      main: '#929292',
      background: '#F0F0F0',
      fadedBackground: '#FAFAFA',
      text: '#212121',
      filledText: '#000000',
    },
    na: {
      main: '#9575CD',
      background: '#EDE7F6',
      fadedBackground: '#F7F2FF',
      text: '#212121',
      filledText: '#000000',
    },
    noWarning: {
      main: '#757575',
      background: '#EAEAEA',
      fadedBackground: '#EAEAEA50',
      text: '#212121',
      filledText: '#FFFFFF',
    },
  },
  'light-grey': {
    main: colors.grey[700],
  },
  snackbars: {
    info: { background: '#0067ba', color: 'white' },
    success: { background: '#008900', color: 'white' },
    warning: { background: '#ffdb00', color: 'black' },
    error: { background: '#d60000', color: 'white' },
  },
};

export const DATA_COLORS: string[] = [
  '#319AD3',
  '#00538A',
  '#859C2B',
  '#008E5B',
  '#FC5D5A',
  '#A90002',
  '#D68100',
  '#A52268',
  '#AB83BE',
  '#5C2993',
  '#5C5C06',
  '#B15928',
];

export const DISABLED_DATA_COLORS: string[] = ['#b8b8b8', '#e2e2e2', '#d5d5d5', '#b8b8b8', '#cecece'];

export const theme = createTheme({
  palette,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      ms: 750,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    // Configure links and buttons to use `react-router-dom`
    // See https://v5.mui.com/material-ui/integrations/routing/#global-theme-link
    MuiLink: { defaultProps: { component: A11yLink, underline: 'always' } as LinkProps },
    MuiButtonBase: { defaultProps: { LinkComponent: A11yLink } },
    MuiButton: {
      variants: [
        {
          props: { variant: 'contained', color: 'light-grey' },
          style: { backgroundColor: colors.grey[200], color: colors.grey[900], '&:hover': { backgroundColor: colors.grey[300] } },
        },
      ],
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.875rem',
          textAlign: 'center',
        },
      },
    },
    MuiTypography: {
      variants: [
        {
          props: { variant: 'caption' },
          style: { letterSpacing: 0, fontSize: '0.8125rem' },
        },
      ],
    },
    MuiSkeleton: {
      defaultProps: TestValueWrapper.wrap({}, { 'data-role': 'skeleton' }),
    },
  },
});
